import { useCallback, useEffect } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Field, FieldProps, Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import { FormikTextField } from '../../components/FormikFields';
import { partnersService } from '../../services/partners.service';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
});

export default function CreateEditPartner() {
  const { modalState, toggleModal } = useModalState('partner');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['partner', modalState.id],
    queryFn: () => partnersService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const isEdit = modalState.reason === 'Update' && !!entity;
  const title = isEdit ? `Edit: ${entity?.name}` : 'Create Partner';

  const initialValues = {
    name: entity?.name || '',
    workspace_id: defaultCreateWorkspace(),
  };

  const copyApiKey = () => {
    if (entity?.api_key) {
      navigator.clipboard.writeText(entity.api_key);
      enqueueSnackbar('API key copied', { variant: 'info' });
    }
  };

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="partner-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              if (isEdit) {
                await partnersService.update(entity!.id, { name: values.name }); // a partner never moves workspace
              } else {
                await partnersService.create(values);
              }
              queryClient.invalidateQueries({ queryKey: ['partners'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, values }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string; values: { workspace_id: string } }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <CopyId id={entity?.id} show={isEdit} sx={{ mb: 1 }} />
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormikTextField name="name" label="Name" fullWidth size="small" />
                  {!isEdit && (
                    <Field name="workspace_id">
                      {({ field, form }: FieldProps<string>) => (
                        <WorkspaceField value={field.value} onChange={(id) => form.setFieldValue('workspace_id', id)} />
                      )}
                    </Field>
                  )}
                  {isEdit && entity?.api_key && (
                    <FormikTextField
                      name="_api_key"
                      label="API Key"
                      fullWidth
                      size="small"
                      value={entity.api_key}
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy API key">
                              <IconButton size="small" onClick={copyApiKey}>
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting || (!isEdit && !values.workspace_id)}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
